// src/components/SettingsPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/contexts/AuthProvider';
import { firstExamSubjects, secondExamSubjects } from '@/constants/subjects';
import SubscriptionManager from './SubscriptionManager';
import PrivacyPolicy from './PrivacyPolicy';
import PersonalInfoProtection from './PersonalInfoProtection';
import CacheManagement from './CacheManagement';
import { useNavigate } from 'react-router-dom';
import { useFirestore } from '@/hooks/useFirestore';

interface HiddenSubjectsSettings {
  '1zi': {
    [subjectId: string]: boolean; // ← true は「非表示」
  };
  '2zi': {
    [subjectId: string]: boolean;
  };
}

interface ExamEnabledSettings {
  enabled_1zi: boolean;
  enabled_2zi: boolean;
}

export const defaultHiddenSubjects: HiddenSubjectsSettings = {
  '1zi': {
    economics: false,
    finance: false,
    management: false,
    operations: false,
    law: false,
    information: false,
    sme: false
  },
  '2zi': {
    case_1: true,
    case_2: true,
    case_3: true,
    case_4: true
  }
};

export const defaultExamEnabled: ExamEnabledSettings = {
  enabled_1zi: true,
  enabled_2zi: true
};


type PageKey = 'none' | 'privacy' | 'personal';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const firestore = useFirestore();
  const navigate = useNavigate();
  const [hiddenSubjects, setHiddenSubjects] =
    useState<HiddenSubjectsSettings>(defaultHiddenSubjects);
  const [examEnabled, setExamEnabled] =
    useState<ExamEnabledSettings>(defaultExamEnabled);
  const [loading, setLoading] = useState(true);

  /* ブックマークリセット用ダイアログ */
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false);
  const [openResetSuccessDialog, setOpenResetSuccessDialog] = useState(false);

  /* 法令ページ表示用 */
  const [selectedPage, setSelectedPage] = useState<PageKey>('none');

  /* ------------------ 設定読み込み ------------------ */
  useEffect(() => {
    // 未ログイン時はデフォルト設定を表示（無限ローディング防止）
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const ref = firestore.getUserDoc(user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setHiddenSubjects(
            data.hidden_subjects || defaultHiddenSubjects
          );
          setExamEnabled(
            data.exam_enabled || defaultExamEnabled
          );
        }
      } catch (e) {
        console.warn('[SettingsPage] 設定読み込みエラー:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  /* ------------------ チェックボックス変更 ------------------ */
  const updateDB = async (data: HiddenSubjectsSettings) => {
    if (!user) return;
    const ref = firestore.getUserDoc(user.uid);
    await setDoc(ref, { hidden_subjects: data }, { merge: true });
  };

  const updateExamEnabledDB = async (data: ExamEnabledSettings) => {
    if (!user) return;
    const ref = firestore.getUserDoc(user.uid);
    await setDoc(ref, { exam_enabled: data }, { merge: true });
  };

  const handleFirstExamChange = (id: string, checked: boolean) => {
    const newData: HiddenSubjectsSettings = {
      ...hiddenSubjects,
      '1zi': { ...hiddenSubjects['1zi'], [id]: !checked } // ← 反転して保存
    };
    setHiddenSubjects(newData);
    updateDB(newData);
  };

  const handleSecondExamChange = (
    id: keyof HiddenSubjectsSettings['2zi'],
    checked: boolean
  ) => {
    const newData: HiddenSubjectsSettings = {
      ...hiddenSubjects,
      '2zi': { ...hiddenSubjects['2zi'], [id]: !checked }
    };
    setHiddenSubjects(newData);
    updateDB(newData);
  };

  const handleExamEnabledChange = (exam: '1zi' | '2zi', checked: boolean) => {
    const newData: ExamEnabledSettings = {
      ...examEnabled,
      [`enabled_${exam}`]: checked
    };
    setExamEnabled(newData);
    updateExamEnabledDB(newData);
  };

  /* ------------------ ブックマークリセット ------------------ */
  const handleResetBookmarksButton = () => setOpenResetConfirmDialog(true);

  const confirmResetBookmarks = async () => {
    if (!user) return;
    try {
      const ref = collection(firestore.getUserDoc(user.uid), 'bookmarks');
      const snap = await getDocs(ref);
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      setOpenResetConfirmDialog(false);
      setOpenResetSuccessDialog(true);
    } catch (e) {
      console.error('ブックマークリセットエラー:', e);
      setOpenResetConfirmDialog(false);
    }
  };

  if (loading) return <Typography>設定を読み込み中...</Typography>;

  return (
    <>
      {/* ▼試験選択 */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼試験選択
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          学習する試験を選択してください。チェックを外した試験の科目はグレーアウトされます。
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={examEnabled.enabled_1zi}
              onChange={(e) => handleExamEnabledChange('1zi', e.target.checked)}
            />
          }
          label="1次試験を有効化"
          sx={{ display: 'block', mb: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={examEnabled.enabled_2zi}
              onChange={(e) => handleExamEnabledChange('2zi', e.target.checked)}
            />
          }
          label="2次試験を有効化"
          sx={{ display: 'block' }}
        />
      </Paper>

      {/* ▼表示問題設定 */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼表示問題設定
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ opacity: examEnabled.enabled_1zi ? 1 : 0.5 }}>
          －１次試験 －
        </Typography>
        {firstExamSubjects.map((s) => (
          <FormControlLabel
            key={s.id}
            control={
              <Checkbox
                checked={!hiddenSubjects['1zi'][s.id]} // ← true なら表示
                onChange={(e) =>
                  handleFirstExamChange(s.id, e.target.checked)
                }
                disabled={!examEnabled.enabled_1zi}
              />
            }
            label={s.name}
            sx={{
              display: 'block',
              opacity: examEnabled.enabled_1zi ? 1 : 0.5
            }}
          />
        ))}

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ opacity: examEnabled.enabled_2zi ? 1 : 0.5 }}>
          －２次試験 － 準備中
        </Typography>
        {secondExamSubjects.map((s) => (
          <FormControlLabel
            key={s.id}
            control={
              <Checkbox
                checked={!hiddenSubjects['2zi'][s.id]} // ← true なら表示
                onChange={(e) =>
                  handleSecondExamChange(
                    s.id as keyof HiddenSubjectsSettings['2zi'],
                    e.target.checked
                  )
                }
                disabled={!examEnabled.enabled_2zi}
              />
            }
            label={s.name}
            sx={{
              display: 'block',
              opacity: examEnabled.enabled_2zi ? 1 : 0.5
            }}
          />
        ))}
      </Paper>

      {/* ▼ブックマークリセット */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼ブックマークリセット
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleResetBookmarksButton}
          >
            ブックマークをリセット
          </Button>
        </Box>
      </Paper>

      {/* ▼キャッシュ管理 */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼キャッシュ管理
        </Typography>
        <Divider sx={{ my: 2 }} />
        <CacheManagement />
      </Paper>

      {/* リセット確認 */}
      <Dialog
        open={openResetConfirmDialog}
        onClose={() => setOpenResetConfirmDialog(false)}
      >
        <DialogTitle>ブックマークリセットの確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当にブックマークをリセットしますか？ (全てのブックマークが削除されます)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetConfirmDialog(false)}>
            キャンセル
          </Button>
          <Button color="secondary" onClick={confirmResetBookmarks}>
            リセット
          </Button>
        </DialogActions>
      </Dialog>

      {/* リセット完了 */}
      <Dialog
        open={openResetSuccessDialog}
        onClose={() => setOpenResetSuccessDialog(false)}
      >
        <DialogTitle>ブックマークリセット完了</DialogTitle>
        <DialogContent>
          <Typography>ブックマークがリセットされました。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetSuccessDialog(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* ▼サブスクリプション管理 */}
      <Paper sx={{ p: 2, m: 2 }}>
        <SubscriptionManager />
      </Paper>

      {/* ▼学習リマインダー */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼学習リマインダー
        </Typography>
        <Divider sx={{ my: 2 }} />
        <List>
          <ListItemButton onClick={() => navigate('/notifications/settings')}>
            <ListItemText
              primary="📢 通知設定"
              secondary="学習リマインダーの頻度や時刻を設定"
            />
          </ListItemButton>
        </List>
      </Paper>

      {/* ▼データ分析 */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼データ分析
        </Typography>
        <Divider sx={{ my: 2 }} />
        <List>
          <ListItemButton onClick={() => navigate('/analytics')}>
            <ListItemText
              primary="学習データ分析"
              secondary="問題ごとの正答率や学習進捗を確認"
            />
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/ga4-analytics')}>
            <ListItemText
              primary="GA4アナリティクス（管理者向け）"
              secondary="アクセス数、流入元、イベント分析"
            />
          </ListItemButton>
        </List>
      </Paper>

      {/* ▼法令に基づくページ */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom>
          ▼法令に基づくページ
        </Typography>
        <Divider sx={{ my: 2 }} />
        <List>
          <ListItemButton onClick={() => setSelectedPage('privacy')}>
            <ListItemText primary="プライバシーポリシー" />
          </ListItemButton>
          <ListItemButton onClick={() => setSelectedPage('personal')}>
            <ListItemText primary="個人情報保護方針" />
          </ListItemButton>
        </List>

        {selectedPage === 'privacy' && <PrivacyPolicy />}
        {selectedPage === 'personal' && <PersonalInfoProtection />}
      </Paper>
    </>
  );
};

export default SettingsPage;
